import React, { useEffect } from 'react';
// libraries
import { isEmpty } from 'lodash';
import PieSocket from 'piesocket-js';
// redux
import { dispatch, useSelector } from '../../redux/store';
import { getNotificationsJwt, setPersistedEmailNotifications, updatePersistedEmailNotification, recentToastKeys, shownNotifIds } from '../../redux/slices/liveNotificationSlice';
import { clearFollowUpTableMoved } from '../../redux/slices/lead';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
import { normalizeActionLink } from '../../layouts/dashboard/header/NotificationsPopover';
// hooks
import { useAuth, useGetCurrentUser } from '../../hooks';
// notifications
import { useNotifications } from '../../sections/notifications/NotificationProvider';

// ── Map verb to a friendly action label for the iOS toast CTA ──
function getActionLabel(verb) {
    if (verb === 'new_email_reply_received') return 'Open Email';
    if (verb === 'new_lead') return 'Open Lead';
    if (verb === 'phone_number_clicked') return 'View';
    if (verb === 'gmb_review_created') return 'Open Reviews';
    if (verb === 'lead_follow_up_moved') return 'Open Lead';
    if (verb === 'whatsapp_message' || verb === 'whatsapp_message_received') return 'Open Chat';
    if (verb === 'whatsapp_template_status') return 'View Templates';
    if (verb === 'annotation_changes_completed') return 'View changes';
    if (verb === 'lead_archived') return 'View Lead';
    // Task V2 notification verbs
    if (verb === 'task_assigned') return 'Open Task';
    if (verb === 'task_due_soon') return 'Open Task';
    if (verb === 'task_due_now') return 'Open Task';
    if (verb === 'task_overdue') return 'Open Task';
    if (verb === 'task_completed') return 'View Task';
    if (verb === 'task_comment') return 'View Comment';
    if (verb === 'form_submitted') return 'View Submission';
    return 'View';
}

// ── Build an action_link for notifications that lack one ──
function buildActionLink(parsedMessage, practiceId, resolvedVerb) {
    // Check verb-specific routes first (before generic action_link fallback)
    const verb = resolvedVerb || parsedMessage?.verb;
    if (verb === 'annotation_changes_completed') {
        return PATH_DASHBOARD.annotationSessions;
    }
    // Task V2 notifications — link to task detail page
    if (verb?.startsWith('task_') && parsedMessage?.data?.task_id && practiceId) {
        return PATH_DASHBOARD.task.tasks.detail(practiceId, parsedMessage.data.task_id);
    }
    if (verb === 'form_submitted') {
        // Submission ID lives on action_object.id (django-notifications-hq sets the GFK)
        const submissionId = parsedMessage?.action_object?.id
            || parsedMessage?.data?.submissionId
            || parsedMessage?.action_object_object_id;
        return submissionId
            ? `${PATH_DASHBOARD.formSubmissions}?cr=${submissionId}`
            : PATH_DASHBOARD.formSubmissions;
    }
    const threadId = parsedMessage?.thread || parsedMessage?.threadId || parsedMessage?.thread_id;
    if (threadId && practiceId) {
        return PATH_DASHBOARD.whatsapp.thread(practiceId, threadId);
    }
    if (parsedMessage?.action_link) {
        return normalizeActionLink(parsedMessage.action_link);
    }
    return undefined;
}

const LiveNotificationHandler = () => {
    const { isAuthenticated } = useAuth();
    const { notificationsJwt } = useSelector((state) => state.liveNotifications);
    const { user } = useGetCurrentUser();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const pieSocket = React.useRef(null);
    const userId = user?.user?.id;
    const { followUpTableMoved } = useSelector((state) => state.lead);
    const { showNotification } = useNotifications();
    // Keep a stable ref so the PieSocket closure always calls the latest function
    const showNotificationRef = React.useRef(showNotification);
    showNotificationRef.current = showNotification;

    useEffect(() => {
        if (isAuthenticated && user?.id && pieSocket?.current === null) {
            dispatch(getNotificationsJwt({ userId: user.id }));
        }
    }, [isAuthenticated, user]);

    useEffect(() => {
        if (notificationsJwt && userId && pieSocket?.current === null) {
            // Validate env vars before connecting — undefined values cause silent
            // connection failures (Sentry: REACT-36, 7 users, 239 events).
            const clusterId = import.meta.env.VITE_REACT_APP_SOCKET_CLUSTER_KEY;
            const apiKey = import.meta.env.VITE_REACT_APP_SOCKET_API_KEY;
            if (!clusterId || !apiKey) {
                console.warn('[PieSocket] Missing VITE_REACT_APP_SOCKET_CLUSTER_KEY or VITE_REACT_APP_SOCKET_API_KEY — skipping WebSocket connection');
                return;
            }
            pieSocket.current = new PieSocket({
                clusterId,
                apiKey,
                jwt: notificationsJwt,
            });
            const handleNotification = (data) => {
                if (!isEmpty(data)) {
                    const parsedMessage = JSON.parse(data);
                    const recipientId = parsedMessage?.recipient_id || parsedMessage?.recipient;
                    if (recipientId && recipientId !== userId) return;

                    // Persist to Redux for the notification panel / bell icon
                    dispatch(setPersistedEmailNotifications(parsedMessage));

                    // Determine the verb — WhatsApp messages may come without one
                    const verb = parsedMessage?.verb
                        || (parsedMessage?.thread || parsedMessage?.threadId || parsedMessage?.thread_id
                            ? 'whatsapp_message_received'
                            : 'new_lead');

                    // Build a resolved action_link (full path for WA, normalised for others)
                    const actionLink = buildActionLink(parsedMessage, practiceId, verb);

                    // WhatsApp toasts are handled by the phone-specific PieSocket
                    // channel in DashboardLayout.  The platform channel uses a
                    // different ID (Django notification ID vs WhatsApp message ID),
                    // so cross-channel dedup can't match.  Skip the toast here to
                    // avoid duplicates — the Redux persist above already updates
                    // the bell icon / notification panel.
                    const isWpVerb = verb === 'whatsapp_message' || verb === 'whatsapp_message_received';
                    if (isWpVerb) return;

                    // Deduplicate non-WhatsApp verbs — when the frontend already
                    // showed a toast (e.g. follow-up table move) PieSocket may
                    // deliver the same event seconds later.  Skip if the key
                    // was registered by the frontend handler within the last 10s.
                    if (!isWpVerb) {
                        const leadId = parsedMessage?.data?.leadId
                            || parsedMessage?.action_object?.id
                            || parsedMessage?.lead_id
                            || '';
                        const dedupKey = `${verb}_${leadId}`;
                        if (leadId && recentToastKeys.has(dedupKey)) return;
                    }

                    // Deduplicate by notification ID — prevents the same notification
                    // showing twice if PieSocket retries or Firebase foreground onMessage
                    // fires for the same event (e.g. treatment_started, deposit_paid).
                    const notifId = parsedMessage?.id ? String(parsedMessage.id) : '';
                    if (notifId) {
                        if (shownNotifIds.has(notifId)) return;
                        shownNotifIds.add(notifId);
                        // Auto-expire after 30s so the set stays small
                        setTimeout(() => shownNotifIds.delete(notifId), 30000);
                    }

                    // Show the new iOS glass morphism toast (via ref to avoid stale closure)
                    showNotificationRef.current({
                        id: parsedMessage.id || `notif_${Date.now()}`,
                        verb,
                        description: parsedMessage?.description || parsedMessage?.body || 'You have a new notification',
                        priority: parsedMessage?.priority || 'normal',
                        requires_action: parsedMessage?.requires_action ?? true,
                        action_label: parsedMessage?.action_label || getActionLabel(verb),
                        action_link: actionLink,
                        data: parsedMessage?.data,
                        action_object: parsedMessage?.action_object,
                    });
                }
            };

            // Quiet live-bump for grouped notifications (e.g. the
            // grouped_count on a same-day phone/WhatsApp click
            // notification getting bumped). Backend pushes this
            // INSTEAD of 'new-notification' for subsequent clicks
            // in the same day, so we update the existing row in
            // Redux without showing a fresh toast.
            const handleNotificationUpdated = (data) => {
                try {
                    if (!data) return;
                    const parsed = JSON.parse(data);
                    const recipientId = parsed?.recipient_id || parsed?.recipient;
                    if (recipientId && recipientId !== userId) return;
                    if (!parsed?.id) return;
                    dispatch(updatePersistedEmailNotification({
                        id: parsed.id,
                        description: parsed.description,
                        grouped_count: parsed.grouped_count,
                        verb: parsed.verb,
                    }));
                } catch (err) {
                    console.warn('[PieSocket] notification-updated parse failed:', err);
                }
            };

            pieSocket.current.subscribe(`${practiceId}_notifications`).catch((err) => {
                console.error('[PieSocket] Failed to subscribe:', err);
                // Reset to null so a future dep change (practice switch, re-login)
                // can reattempt. No automatic retry within this session — deps
                // [notificationsJwt, userId, practiceId] don't change on failure.
                pieSocket.current = null;
            }).then((channel) => {
                if (!channel) return; // Subscription failed (caught above)
                channel.listen('new-notification', handleNotification);
                channel.listen('notification-updated', handleNotificationUpdated);
            });

            // Per-user task channel added in BE commit 3c8ce50c — subscribe independently
            // so a failure here does not affect the platform notifications channel.
            if (pieSocket.current) {
                pieSocket.current.subscribe(`${practiceId}_user_${userId}_tasks`).catch((err) => {
                    console.error('[PieSocket] Failed to subscribe to task channel:', err);
                }).then((channel) => {
                    if (!channel) return;
                    channel.listen('new-notification', handleNotification);
                });
            }
        }
    }, [notificationsJwt, userId, practiceId]);

    // ── Follow-up table move toast (frontend-only detection) ──
    // When addNotes detects that contacted_count crossed a table threshold
    // (e.g. 0→1 means New → Follow up 1), it sets followUpTableMoved in Redux.
    // We pick it up here and fire an iOS toast, then clear the flag.
    useEffect(() => {
        if (followUpTableMoved) {
            // Register dedup key so the PieSocket duplicate (if any) is skipped
            const dedupKey = `lead_follow_up_moved_${followUpTableMoved.leadId}`;
            recentToastKeys.add(dedupKey);
            setTimeout(() => recentToastKeys.delete(dedupKey), 10000);

            // Enhanced description — include "Note added" when the move was
            // triggered by addNotes so the user gets one combined toast instead
            // of a separate snackbar + toast.
            const description = followUpTableMoved.noteAdded
                ? `Note added — ${followUpTableMoved.leadName} moved to ${followUpTableMoved.newLabel}`
                : `${followUpTableMoved.leadName} moved to ${followUpTableMoved.newLabel}`;

            showNotificationRef.current({
                id: `table_move_${followUpTableMoved.leadId}_${Date.now()}`,
                verb: 'lead_follow_up_moved',
                description,
                priority: 'normal',
                requires_action: true,
                action_label: 'Open Lead',
                action_link: practiceId
                    ? `/dashboard/practice/${practiceId}/lead-detail/${followUpTableMoved.leadId}`
                    : undefined,
                data: { leadId: followUpTableMoved.leadId },
            });
            dispatch(clearFollowUpTableMoved());
        }
    }, [followUpTableMoved, practiceId]);

    // All rendering is handled by NotificationProvider / NotificationToastContainer
    return null;
};

export default LiveNotificationHandler;
